import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowsClockwise, CheckCircle, Warning, Broadcast, Clock, UploadSimple } from "@phosphor-icons/react";
import { api } from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "never";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function KraaftReconciliationWidget() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/kraaft-reconciliation")
      .then(r => setData(r.data))
      .catch(() => setData(null));
  }, []);

  if (!data) return null;
  const inSync = data.stale_open === 0 && data.last_sync_at;
  const bg = inSync ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50";
  const iconClr = inSync ? "text-emerald-600" : "text-amber-600";
  const StatusIcon = inSync ? CheckCircle : Warning;
  const hasStale = (data.stale_open || 0) > 0;

  return (
    <div className={`mt-4 rounded-lg border ${bg} p-4`} data-testid="kraaft-recon-widget">
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-full bg-white border-2 ${inSync ? "border-emerald-500" : "border-amber-500"} flex items-center justify-center flex-shrink-0`}>
          <StatusIcon weight="fill" size={20} className={iconClr}/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <Broadcast weight="fill" size={12} className="text-cyan-500"/>
            AppFolio · Kraaft Sync
          </div>
          <div className="text-sm font-black text-slate-900 mt-0.5">
            {inSync ? "In sync" : `${data.stale_open} WOs may be out of sync`}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
            <Clock weight="bold" size={11}/> Last import {timeAgo(data.last_sync_at)}
            {data.last_sync_by && <span>· by {data.last_sync_by}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to="/admin/jobs?import=1"
            data-testid="recon-import-csv"
            className="inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-black uppercase tracking-widest px-2.5 py-1.5 rounded"
          >
            <UploadSimple weight="bold" size={12}/> Import
          </Link>
          <Link
            to="/admin/csv-sync-history"
            data-testid="recon-view-history"
            className="text-cyan-700 hover:text-cyan-900 text-[10px] font-black uppercase tracking-widest"
          >
            History →
          </Link>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <Cell label="Auto-closed" val={data.auto_closed_last_sync || 0} tone="violet" testid="recon-auto-closed"/>
        <StaleCell val={data.stale_open || 0} clickable={hasStale}/>
        <Cell label="FR-only" val={data.never_synced || 0} tone="slate" testid="recon-fr-only"/>
      </div>
      {!data.last_sync_at && (
        <div className="mt-2 text-[11px] text-slate-500 italic">
          No AppFolio sync yet —{" "}
          <Link to="/admin/jobs?import=1" className="text-cyan-700 underline font-bold" data-testid="recon-empty-import">
            import a CSV
          </Link>{" "}
          to start tracking closures.
        </div>
      )}
    </div>
  );
}

function Cell({ label, val, tone, testid }) {
  const tones = {
    violet: "text-violet-700",
    amber: "text-amber-700",
    slate: "text-slate-600",
  };
  return (
    <div data-testid={testid}>
      <div className={`text-xl font-black tabular-nums ${tones[tone]}`}>{val}</div>
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{label}</div>
    </div>
  );
}

function StaleCell({ val, clickable }) {
  const tone = val > 0 ? "text-amber-700" : "text-slate-600";
  const body = (
    <>
      <div className={`text-xl font-black tabular-nums ${tone} ${clickable ? "underline decoration-dotted underline-offset-4" : ""}`}>{val}</div>
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Stale open</div>
    </>
  );
  if (!clickable) {
    return <div data-testid="recon-stale">{body}</div>;
  }
  return (
    <Link
      to="/admin/jobs?stale=1"
      data-testid="recon-stale"
      className="block hover:bg-amber-100 rounded transition-colors"
      title="See the WOs Kraaft may have already closed"
    >
      {body}
    </Link>
  );
}
